import DictionaryForm from "@/components/dictionaries/DictionaryForm";
import Field from "@/components/Field";
import Fieldset from "@/components/Fieldset";
import PageContent from "@/components/PageContent";
import { Input } from "@/components/ui/input";
import { isRequired } from "@/lib/form";
import { useAgreementTypesDictionary } from "@/modules/agreement-types/hooks/useAgreementTypesDictionary";
import { useTranslation } from "react-i18next";
import SaveButton from "@/components/SaveButton";
import AgreementCompanySelect from "@/components/AgreementCompanySelect";
import DatePicker from "@/components/DatePicker";
import CheckboxField from "@/components/CheckboxField";
import { useDictionaryContext } from "@/components/dictionaries/DictionaryContext";
import { AgreementType, AgreementTypeUpdate } from "@/types/agreementType";
import { NumberInput } from "@/components/ui/NumberInput";

interface AgreementTypeFormProps {
  footer?: React.ReactNode;
}

export default function AgreementTypeForm({
  footer = <SaveButton />,
}: AgreementTypeFormProps) {
  const { t } = useTranslation();
  const config = useAgreementTypesDictionary();
  const { Icon, ns } = config;
  const labels = config.getLabels(t);
  const { query } = useDictionaryContext<AgreementType, AgreementTypeUpdate>();

  return (
    <DictionaryForm>
      <PageContent
        icon={<Icon className="w-6 h-6 mr-3" />}
        title={labels.title}
        footer={footer}
      >
        <Fieldset className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field
            name="name"
            label={t(`${ns}.fields.name`)}
            required={isRequired(config.schema.shape.name)}
            render={({ field }) => <Input className="w-full" {...field} />}
          />

          <Field
            name="company_id"
            label={t(`${ns}.fields.company`)}
            required={isRequired(config.schema.shape.company_id)}
            render={({ field }) => (
              <AgreementCompanySelect
                className="w-full"
                defaultItem={{
                  id: query?.data?.company_id,
                  name: query?.data?.company_name,
                }}
                {...field}
              />
            )}
          />

          <Field
            name="template_code"
            label={t(`${ns}.fields.template_code`)}
            required={isRequired(config.schema.shape.template_code)}
            render={({ field }) => <Input className="w-full" {...field} />}
          />

          <Field
            name="valid_from"
            label={t(`${ns}.fields.valid_from`)}
            required={isRequired(config.schema.shape.valid_from)}
            render={({ field }) => <DatePicker className="w-full" {...field} />}
          />

          <Field
            name="valid_to"
            label={t(`${ns}.fields.valid_to`)}
            required={isRequired(config.schema.shape.valid_to)}
            render={({ field }) => <DatePicker className="w-full" {...field} />}
          />

          <Field
            name="sort_order"
            label={t(`${ns}.fields.sort_order`)}
            required={isRequired(config.schema.shape.sort_order)}
            render={({ field }) => (
              <NumberInput className="w-full" {...field} />
            )}
          />

          <CheckboxField
            name="active"
            label={t(`${ns}.fields.active`)}
            required={isRequired(config.schema.shape.active)}
          />

          <CheckboxField
            name="contest_regulations_required"
            label={t(`${ns}.fields.contest_regulations_required`)}
            required={isRequired(
              config.schema.shape.contest_regulations_required,
            )}
          />

          <CheckboxField
            name="is_b2b"
            label={t(`${ns}.fields.is_b2b`)}
            required={isRequired(config.schema.shape.is_b2b)}
          />
        </Fieldset>
      </PageContent>
    </DictionaryForm>
  );
}
